/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { Checkbox, Divider, Input as AntdInput, Tag } from 'antd';
import { useState } from 'react';

import { Node } from '@/client';
import { TagsField } from '@/components/TagsField';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { destinationDisplayNames, DestinationType, monoTagStyle } from './constants';
import { CreateAlertWizardFooter } from './CreateAlertWizardFooter';
import { SelectNodesModal } from './SelectNodesModal';

type EventType = 'signal' | 'deflection' | 'hallmark' | 'lifecycle';

type SetParametersStepProps = {
  destinationType: DestinationType;
  eventTypes: EventType[];
  selectedNodes: Node[];
  selectedTags: string[];
  includeFilters: Record<string, string>;
  excludeFilters: Record<string, string>;
  onEventTypesChange: (types: EventType[]) => void;
  onSelectedNodesChange: (nodes: Node[]) => void;
  onSelectedTagsChange: (tags: string[]) => void;
  onIncludeFiltersChange: (filters: Record<string, string>) => void;
  onExcludeFiltersChange: (filters: Record<string, string>) => void;
  onNext: () => void;
  onPrevious: () => void;
};

export function SetParametersStep({
  destinationType,
  eventTypes,
  selectedNodes,
  selectedTags,
  includeFilters,
  excludeFilters,
  onEventTypesChange,
  onSelectedNodesChange,
  onSelectedTagsChange,
  onIncludeFiltersChange,
  onExcludeFiltersChange,
  onNext,
  onPrevious,
}: SetParametersStepProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const [nodesModalOpen, setNodesModalOpen] = useState(false);
  const [newIncludeKey, setNewIncludeKey] = useState('');
  const [newIncludeValue, setNewIncludeValue] = useState('');
  const [newExcludeKey, setNewExcludeKey] = useState('');
  const [newExcludeValue, setNewExcludeValue] = useState('');
  const displayName = destinationDisplayNames[destinationType];

  const handleEventTypeToggle = (eventType: EventType) => {
    if (eventTypes.includes(eventType)) {
      onEventTypesChange(eventTypes.filter((et) => et !== eventType));
    } else {
      onEventTypesChange([...eventTypes, eventType]);
    }
  };

  const handleRemoveNode = (nodeId: string) => {
    onSelectedNodesChange(selectedNodes.filter((n) => n.id !== nodeId));
  };

  const handleNodesConfirm = (nodes: Node[]) => {
    onSelectedNodesChange(nodes);
    setNodesModalOpen(false);
  };

  const handleAddIncludeFilter = () => {
    if (newIncludeKey && newIncludeValue) {
      onIncludeFiltersChange({ ...includeFilters, [newIncludeKey]: newIncludeValue });
      setNewIncludeKey('');
      setNewIncludeValue('');
    }
  };

  const handleRemoveIncludeFilter = (key: string) => {
    const updated = { ...includeFilters };
    delete updated[key];
    onIncludeFiltersChange(updated);
  };

  const handleAddExcludeFilter = () => {
    if (newExcludeKey && newExcludeValue) {
      onExcludeFiltersChange({ ...excludeFilters, [newExcludeKey]: newExcludeValue });
      setNewExcludeKey('');
      setNewExcludeValue('');
    }
  };

  const handleRemoveExcludeFilter = (key: string) => {
    const updated = { ...excludeFilters };
    delete updated[key];
    onExcludeFiltersChange(updated);
  };

  const handleAddTag = (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      onSelectedTagsChange([...selectedTags, tagName]);
    }
  };

  const handleRemoveTag = (tagName: string) => {
    onSelectedTagsChange(selectedTags.filter((tag) => tag !== tagName));
  };

  const eventTypeItems: { key: EventType; label: string; description: string; warning?: string }[] = [
    {
      key: 'signal',
      label: t('parameters.events.signal'),
      description: t('parameters.events.signalDescription'),
      warning: t('parameters.events.signalWarning'),
    },
    {
      key: 'deflection',
      label: t('parameters.events.deflection'),
      description: t('parameters.events.deflectionDescription'),
    },
    {
      key: 'hallmark',
      label: t('parameters.events.hallmark'),
      description: t('parameters.events.hallmarkDescription'),
    },
    {
      key: 'lifecycle',
      label: t('parameters.events.lifecycle'),
      description: t('parameters.events.lifecycleDescription'),
    },
  ];

  const isValid = eventTypes.length > 0;

  return (
    <>
      <div className="create-alert-wizard-content">
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 0' }}>
          <Title level={3}>{t('parameters.title')}</Title>
          <Text type="subtle" size="md">
            {t('parameters.subtitle', { destination: displayName })}
          </Text>

          <div style={{ marginTop: '32px' }}>
            <Title level={5}>{t('parameters.eventTypesTitle')}</Title>
            <Text type="subtle" size="sm">
              {t('parameters.eventTypesDescription')}
            </Text>

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {eventTypeItems.map((event) => (
                <div
                  key={event.key}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1 }}>
                    <Text strong size="md">
                      {event.label}
                    </Text>
                    <br />
                    <Text type="subtle" size="sm">
                      {event.description}
                    </Text>
                    {event.warning && eventTypes.includes(event.key) && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="subtle" size="sm">
                          <WarningOutlined style={{ color: '#d4b25a', marginRight: '4px' }} />
                          {event.warning}
                        </Text>
                      </div>
                    )}
                  </div>
                  <Checkbox
                    checked={eventTypes.includes(event.key)}
                    onChange={() => handleEventTypeToggle(event.key)}
                    data-testid={`event-type-${event.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <Divider />

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Title level={5}>{t('parameters.nodesTitle')}</Title>
                <Text type="subtle" size="sm">
                  {t('parameters.nodesDescription')}
                </Text>
              </div>
              <Button type="default" onClick={() => setNodesModalOpen(true)} dataTestId="add-nodes-button">
                {t('parameters.addNodes')}
              </Button>
            </div>

            {selectedNodes.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedNodes.map((node) => (
                  <Tag
                    key={node.id}
                    closable
                    onClose={() => handleRemoveNode(node.id)}
                    closeIcon={<CloseOutlined />}
                    style={monoTagStyle}
                  >
                    {node.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginTop: '24px' }}>
            <Title level={5}>{t('parameters.tagsTitle')}</Title>
            <Text type="subtle" size="sm">
              {t('parameters.tagsDescription')}
            </Text>

            <div style={{ marginTop: '12px' }}>
              <TagsField
                selectedTags={selectedTags}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
                allowCreate={false}
              />
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Title level={5}>{t('parameters.includeTitle')}</Title>
            <Text type="subtle" size="sm">
              {t('parameters.includeDescription')}
            </Text>

            {Object.entries(includeFilters).length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(includeFilters).map(([key, value]) => (
                  <Tag
                    key={key}
                    closable
                    onClose={() => handleRemoveIncludeFilter(key)}
                    closeIcon={<CloseOutlined />}
                    style={monoTagStyle}
                  >
                    {key}: {value}
                  </Tag>
                ))}
              </div>
            )}

            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <AntdInput
                placeholder={t('parameters.keyPlaceholder')}
                value={newIncludeKey}
                onChange={(e) => setNewIncludeKey(e.target.value)}
                style={{ width: 160, backgroundColor: '#ffffff' }}
                data-testid="include-key-input"
              />
              <AntdInput
                placeholder={t('parameters.valuePlaceholder')}
                value={newIncludeValue}
                onChange={(e) => setNewIncludeValue(e.target.value)}
                style={{ width: 200, backgroundColor: '#ffffff' }}
                data-testid="include-value-input"
              />
              <Button
                type="default"
                onClick={handleAddIncludeFilter}
                disabled={!newIncludeKey || !newIncludeValue}
                dataTestId="add-include-filter"
              >
                {t('parameters.add')}
              </Button>
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Title level={5}>{t('parameters.excludeTitle')}</Title>
            <Text type="subtle" size="sm">
              {t('parameters.excludeDescription')}
            </Text>

            {Object.entries(excludeFilters).length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(excludeFilters).map(([key, value]) => (
                  <Tag
                    key={key}
                    closable
                    onClose={() => handleRemoveExcludeFilter(key)}
                    closeIcon={<CloseOutlined />}
                    style={monoTagStyle}
                  >
                    {key}: {value}
                  </Tag>
                ))}
              </div>
            )}

            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <AntdInput
                placeholder={t('parameters.keyPlaceholder')}
                value={newExcludeKey}
                onChange={(e) => setNewExcludeKey(e.target.value)}
                style={{ width: 160, backgroundColor: '#ffffff' }}
                data-testid="exclude-key-input"
              />
              <AntdInput
                placeholder={t('parameters.valuePlaceholder')}
                value={newExcludeValue}
                onChange={(e) => setNewExcludeValue(e.target.value)}
                style={{ width: 200, backgroundColor: '#ffffff' }}
                data-testid="exclude-value-input"
              />
              <Button
                type="default"
                onClick={handleAddExcludeFilter}
                disabled={!newExcludeKey || !newExcludeValue}
                dataTestId="add-exclude-filter"
              >
                {t('parameters.add')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <CreateAlertWizardFooter
        onNext={onNext}
        onPrevious={onPrevious}
        nextLabel={t('parameters.nextLabel')}
        previousLabel={t('parameters.previousLabel', { destination: displayName })}
        nextDisabled={!isValid}
      />

      <SelectNodesModal
        open={nodesModalOpen}
        initialSelectedNodes={selectedNodes}
        onClose={() => setNodesModalOpen(false)}
        onConfirm={handleNodesConfirm}
      />
    </>
  );
}
